import type { ConversationOverview } from '@/types/messages.ts';

/**
 * One row of the conversation picker: either a whole channel — every
 * conversation this person has had there, read as one transcript — or a single
 * conversation.
 */
export interface ConversationGroup {
  type: 'channel' | 'conversation';
  id: string;
  channel: string;
  agent: string;
  is_current: boolean;
  message_count: number;
  last_message_at: string | null;
  conversations: ConversationOverview[];
}

function newestFirst(
  a: { last_message_at: string | null },
  b: { last_message_at: string | null },
) {
  const left = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
  const right = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
  return right - left;
}

/**
 * The conversations as the picker offers them.
 *
 * Grouped by channel a person reads as one thread per place they talk to us,
 * which is how they experience it; ungrouped, every conversation stands on its
 * own — which is what you want when you are looking for one particular run.
 */
export function groupConversations(
  conversations: ConversationOverview[],
  groupByChannel: boolean,
): ConversationGroup[] {
  if (!groupByChannel) {
    return conversations.map((conversation) => ({
      type: 'conversation',
      id: conversation.id,
      channel: conversation.channel,
      agent: conversation.agent,
      is_current: conversation.is_current,
      message_count: conversation.message_count,
      last_message_at: conversation.last_message_at,
      conversations: [conversation],
    }));
  }

  const byChannel = new Map<string, ConversationOverview[]>();

  for (const conversation of conversations) {
    const group = byChannel.get(conversation.channel);
    if (group) {
      group.push(conversation);
    } else {
      byChannel.set(conversation.channel, [conversation]);
    }
  }

  return [...byChannel.entries()]
    .map(([channel, group]): ConversationGroup => ({
      type: 'channel',
      id: `channel-${channel}`,
      channel,
      agent: group.length > 1 ? `${group.length} conversations` : group[0].agent,
      is_current: group.some((conversation) => conversation.is_current),
      message_count: group.reduce(
        (total, conversation) => total + conversation.message_count,
        0,
      ),
      last_message_at:
        group
          .map((conversation) => conversation.last_message_at)
          .filter((value): value is string => Boolean(value))
          .sort()
          .reverse()[0] ?? null,
      conversations: group,
    }))
    .sort(newestFirst);
}

/**
 * The group holding a conversation, by either the group's own id or the id of
 * a conversation inside it — a `?conversation=` in the URL names the second.
 */
export function findGroup(
  groups: ConversationGroup[],
  id: string | null,
): ConversationGroup | null {
  if (!id) return null;

  return (
    groups.find(
      (group) =>
        group.id === id ||
        group.conversations.some((conversation) => conversation.id === id),
    ) ?? null
  );
}

/** Newest conversation first — the order a merged transcript is read back in. */
export function orderedConversations(group: ConversationGroup): ConversationOverview[] {
  return [...group.conversations].sort(newestFirst);
}

/**
 * Where the transcript crosses from one conversation into the next, by index
 * into the messages being shown. Only meaningful for a merged channel: a single
 * conversation never switches.
 */
export function conversationSwitches(
  messages: { conversation_id: string }[],
  conversations: ConversationOverview[],
  merged: boolean,
): Map<number, ConversationOverview> {
  const switches = new Map<number, ConversationOverview>();
  if (!merged) return switches;

  let previous: string | null = null;

  messages.forEach((message, index) => {
    if (message.conversation_id === previous) return;

    const conversation = conversations.find((one) => one.id === message.conversation_id);
    /* Never before the very first message — nothing was switched away from. */
    if (conversation && previous !== null) switches.set(index, conversation);
    previous = message.conversation_id;
  });

  return switches;
}
